import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_EVENT = 'lpc-badges-refresh';
export const BADGES_REFRESH_EVENT = REFRESH_EVENT;

// Force un refetch immediat des pastilles partout (apres lecture, envoi, etc.)
export const notifyBadgesChanged = () => {
  window.dispatchEvent(new Event(REFRESH_EVENT));
};

// Mode apercu (manager "voir comme"), positionne par AuthContext : les pastilles
// affichees sont celles du salarie vu, donc marquer "lu" est desactive (le POST
// ne porte pas view_as et modifierait l'etat de lecture du manager, pas le sien).
let _previewMode = false;
export const setBadgesPreviewMode = (active) => { _previewMode = !!active; };

// Ouvrir un module = ses notifications passent en lues, la pastille s'efface
export async function markModuleSeen(types) {
  if (_previewMode) return;
  try {
    await axios.post(`${API}/notifications/mark-read-by-types`, { types });
    notifyBadgesChanged();
  } catch (e) {
    // silencieux : la pastille retombera au prochain poll
  }
}

// Marque lues UNIQUEMENT les notifications d'un module qui ne sont rattachees a
// aucun objet. Sert aux ecrans qui affichent une pastille par ligne : les
// anciennes notifications, sans objet, n'ont nulle part ou s'afficher, elles ne
// doivent donc pas rester rouges indefiniment.
export async function markOrphanNotificationsSeen(types) {
  if (_previewMode) return;
  try {
    await axios.post(`${API}/notifications/mark-read-by-entity`, { types, entity_id: null });
    notifyBadgesChanged();
  } catch (e) {
    // silencieux
  }
}

// Compteurs de non-lus par OBJET, pour une pastille sur chaque ligne d'un ecran
// (un vehicule, une machine...). Renvoie la table des compteurs et la fonction
// qui efface celle d'un objet quand on l'ouvre.
export function useEntityBadges(types) {
  const [entityBadges, setEntityBadges] = useState({});
  const key = (types || []).join(',');

  const fetchEntityBadges = useCallback(async () => {
    if (!key) return;
    try {
      const res = await axios.get(`${API}/notifications/unread-by-entity`, { params: { types: key } });
      setEntityBadges(res.data?.entities || {});
    } catch (e) {
      // silencieux
    }
  }, [key]);

  useEffect(() => {
    fetchEntityBadges();
    const interval = setInterval(fetchEntityBadges, 30000);
    const onVisible = () => { if (!document.hidden) fetchEntityBadges(); };
    window.addEventListener(REFRESH_EVENT, fetchEntityBadges);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener(REFRESH_EVENT, fetchEntityBadges);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchEntityBadges]);

  // Ouvrir une ligne efface SA pastille (et met a jour le total du module).
  const clearEntityBadge = useCallback(async (entityId) => {
    if (_previewMode || !entityId) return;
    setEntityBadges(prev => {
      if (!prev[entityId]) return prev;
      const next = { ...prev };
      delete next[entityId];
      return next;
    });
    try {
      await axios.post(`${API}/notifications/mark-read-by-entity`, { types, entity_id: entityId });
      notifyBadgesChanged();
    } catch (e) {
      // silencieux : la pastille reviendra au prochain poll
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { entityBadges, clearEntityBadge };
}

// Compteurs de non-lus par module ({ chat: 3, materials: 1, ... })
export function useModuleBadges() {
  const [badges, setBadges] = useState({});

  const fetchBadges = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-by-module`);
      setBadges(res.data?.modules || {});
    } catch (e) {
      // silencieux (hors-ligne, session expiree...)
    }
  }, []);

  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, 30000);
    const onVisible = () => { if (!document.hidden) fetchBadges(); };
    window.addEventListener(REFRESH_EVENT, fetchBadges);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener(REFRESH_EVENT, fetchBadges);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchBadges]);

  return badges;
}
